// @ts-nocheck
// @ts-ignore
class CustomCartControllers extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this._quantity = 0;
        this._isUpdating = false;
        this._lineKey = null;
        this._variantId = null;

        if (!this.shadowRoot) {
            throw new Error('Failed to create shadow root');
        }

        this.shadowRoot.innerHTML = `
        <style>
            :host {
                display: block;
            }

            :host(.is-loading) .cart-item-card__controllers--quantity-button,
            :host(.is-loading) .cart-item-card__controllers--remove-button {
                opacity: 0.6;
                cursor: not-allowed;
                pointer-events: none;
            }

            .cart-item-card__controllers--quantity-count slot {
                display: none;
            }

            .cart-item-card__controllers {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .cart-item-card__controllers--quantity {
                display: flex;
                align-items: center;
                border: 1px solid black;
                border-radius: 5px;
                width: 90px;
                height: 32px;
            }

            .cart-item-card__controllers--quantity-button {
                width: 32px;
                border: none;
                background-color: transparent;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 18px;
                padding: 0;
                transition: opacity 0.2s;
                line-height: 0.325;
                color: #000;
            }

            .cart-item-card__controllers--quantity-button:hover:not(:disabled) {
                opacity: 0.7;
            }

            .cart-item-card__controllers--quantity-button:disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }

            .cart-item-card__controllers--quantity-count {
                min-width: 32px;
                text-align: center;
                padding: 0 8px;
                border: none;
                background-color: transparent;
                font-size: 14px;
                font-weight: 600;
                font-family: "Athletics-Bold", sans-serif;
            }

            .cart-item-card__controllers--remove-button {
                display: flex;
                align-items: center;
                gap: 4px;
                border: none;
                background-color: transparent;
                cursor: pointer;
                color: red;
                font-size: 11px;
                font-weight: 500;
                transition: opacity 0.2s;
                font-family: 'Athletics-Regular', sans-serif;
            }

            .cart-item-card__controllers--remove-button:hover:not(:disabled) {
                opacity: 0.7;
            }

            .cart-item-card__controllers--remove-button:disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }

            .cart-item-card__controllers--remove-icon {
                width: 11px;
                height: 11px;
                flex-shrink: 0;
            }

            .hide {
                display: none;
            }

            @media (max-width: 600px) {
                .cart-item-card__controllers--quantity {
                    width: 80px;
                    height: 28px;
                    padding: 2px 4px;
                }
                .cart-item-card__controllers--remove-icon {
                    width: 12px;
                    height: 12px;
                }
                .cart-item-card__controllers--remove-button {
                    font-size: 12px;
                    padding: 0 4px;
                    gap: 5px;
                }
            }


        </style>

        <div class="cart-item-card__controllers">
            <div class="cart-item-card__controllers--quantity">
                <button type="button" class="cart-item-card__controllers--quantity-button" aria-label="Decrement quantity" data-action="decrement">-</button>
                <span class="cart-item-card__controllers--quantity-count">
                    <slot></slot>
                </span>
                <button type="button" class="cart-item-card__controllers--quantity-button" aria-label="Increment quantity" data-action="increment">+</button>
            </div>

            <div class="loader loading-overlay__spinner hide">
              <custom-cart-loader></custom-cart-loader>
            </div>

            <button type="button" class="cart-item-card__controllers--remove-button" aria-label="Remove item from cart" data-action="remove">
                <svg class="cart-item-card__controllers--remove-icon" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5 3.5H11.5M10.5 3.5V11.5C10.5 12.0523 10.0523 12.5 9.5 12.5H4.5C3.94772 12.5 3.5 12.0523 3.5 11.5V3.5M5.25 3.5V2.5C5.25 1.94772 5.69772 1.5 6.25 1.5H7.75C8.30228 1.5 8.75 1.94772 8.75 2.5V3.5M5.5 6V9.5M8.5 6V9.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                REMOVE
            </button>
        </div>
        `;
    }

    connectedCallback() {
        // Initialize quantity from data attributes or slot
        this._initializeQuantity();

        // Get data attributes
        const lineKeyAttr = this.getAttribute('data-line-key');
        this._lineKey = lineKeyAttr || null;
        const variantIdAttr = this.getAttribute('data-variant-id');
        this._variantId = variantIdAttr || null;
        // Loader lives inside shadow DOM
        this._loader = this.shadowRoot?.querySelector('.loader') || null;

        // Validate required attribute
        if (!this._lineKey) {
            console.error('custom-cart-controllers: data-line-key attribute is required');
            return;
        }

        // Update the count display
        this._updateCountDisplay();

        // Attach event listeners
        this._attachEventListeners();
    }

    disconnectedCallback() {
        // Clean up event listeners if needed
        if (!this.shadowRoot) return;
        const buttons = this.shadowRoot.querySelectorAll('button');
        buttons.forEach(button => {
            button.removeEventListener('click', this._handleButtonClick);
        });
    }

    static get observedAttributes() {
        return ['data-quantity'];
    }

    /**
     * @param {string} name
     * @param {string|null} oldValue
     * @param {string|null} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'data-quantity' && oldValue !== newValue && newValue !== null) {
            const newQuantity = parseInt(newValue, 10);
            if (!isNaN(newQuantity) && newQuantity >= 0) {
                this._quantity = newQuantity;
                this._updateCountDisplay();
            }
        }
    }

    _initializeQuantity() {
        if (!this.shadowRoot) return;

        // Priority 1: data-quantity attribute
        const dataQuantity = this.getAttribute('data-quantity');
        if (dataQuantity !== null) {
            const parsed = parseInt(dataQuantity, 10);
            if (!isNaN(parsed) && parsed >= 0) {
                this._quantity = parsed;
                return;
            }
        }

        // Priority 2: Slotted text content
        const slot = this.shadowRoot.querySelector('slot');
        if (slot) {
            const assignedNodes = slot.assignedNodes();
            if (assignedNodes.length > 0 && assignedNodes[0]) {
                const textContent = assignedNodes[0].textContent ? assignedNodes[0].textContent.trim() : '';
                if (textContent) {
                    const parsed = parseInt(textContent, 10);
                    if (!isNaN(parsed) && parsed >= 0) {
                        this._quantity = parsed;
                        return;
                    }
                }
            }
        }

        // Default to 1 if nothing found
        this._quantity = 1;
    }

    _updateCountDisplay() {
        if (!this.shadowRoot) return;
        const countElement = this.shadowRoot.querySelector('.cart-item-card__controllers--quantity-count');
        if (countElement) {
            // Clear slot content and set direct text
            if(this._quantity != 0){
                countElement.textContent = this._quantity.toString();
            }
        }
    }

    _attachEventListeners() {
        if (!this.shadowRoot) return;
        const buttons = this.shadowRoot.querySelectorAll('button[data-action]');
        buttons.forEach(button => {
            button.addEventListener('click', this._handleButtonClick.bind(this));
        });
    }

    /**
     * @param {Event} event
     */
    _handleButtonClick(event) {
        if (this._isUpdating || !event.currentTarget) {
            return; // Ignore clicks while update is in progress
        }

        const target = /** @type {HTMLElement} */ (event.currentTarget);
        const action = target.getAttribute('data-action');
        if (!action) return;

        switch (action) {
            case 'increment':
                this._updateCart('increment', 1);
                break;
            case 'decrement':
                if (this._quantity === 1) {
                    this._updateCart('decrement', 0);
                } else {
                    this._updateCart('decrement', 1);
                }
                break;
            case 'remove':
                this._updateCart('remove', 0);
                break;
        }
    }

    /**
     * @param {string} action
     * @param {number} quantity
     */
    async _updateCart(action, quantity = 1) {
        if (this._isUpdating) {
            return;
        }

        // Calculate new quantity
        let newQuantity;
        if (action === 'remove' || (action === 'decrement' && quantity === 0)) {
            newQuantity = 0;
        } else if (action === 'increment') {
            newQuantity = this._quantity + quantity;
        } else if (action === 'decrement') {
            newQuantity = Math.max(0, this._quantity - quantity);
        } else {
            newQuantity = this._quantity;
        }

        // Store current quantity for potential revert
        const previousQuantity = this._quantity;

        // Update UI optimistically
        this._quantity = newQuantity;
        this._updateCountDisplay();
        this._setLoadingState(true);

        // Show the loader for this cart item card
        this._showLoader();

        try {
            // Find the cart-items-component to get section ID
            const cartItemsComponent = this._findCartItemsComponent();
            const sectionId = cartItemsComponent?.dataset?.sectionId;

            // Build request body - use sections API if section ID is available
            let requestBody;
            let endpoint;

            if (sectionId) {
                // Use sections API to get updated section HTML
                const cartItemsComponents = document.querySelectorAll('cart-items-component');
                const sectionsToUpdate = new Set([sectionId]);
                cartItemsComponents.forEach((item) => {
                    if (item instanceof HTMLElement && item.dataset.sectionId) {
                        sectionsToUpdate.add(item.dataset.sectionId);
                    }
                });

                // Get line number from data-index attribute on parent cart-item-card
                const cartItemCard = this.closest('.cart-item-card');
                const lineIndex = cartItemCard && cartItemCard instanceof HTMLElement && cartItemCard.dataset?.index
                    ? parseInt(cartItemCard.dataset.index, 10)
                    : null;

                if (lineIndex) {
                    requestBody = JSON.stringify({
                        line: lineIndex,
                        quantity: newQuantity,
                        sections: Array.from(sectionsToUpdate).join(','),
                        sections_url: window.location.pathname,
                    });
                    endpoint = typeof Theme !== 'undefined' && Theme.routes?.cart_change_url
                        ? Theme.routes.cart_change_url
                        : '/cart/change';
                } else {
                    // Fallback to old method if line index not found
                    requestBody = JSON.stringify({
                        id: this._lineKey,
                        quantity: newQuantity
                    });
                    endpoint = '/cart/change.js';
                }
            } else {
                // Fallback to old method if section ID not found
                requestBody = JSON.stringify({
                    id: this._lineKey,
                    quantity: newQuantity
                });
                endpoint = '/cart/change.js';
            }

            // Try to import fetchConfig, otherwise use basic config
            let fetchOptions;
            try {
                const { fetchConfig: fetchConfigUtil } = await import('@theme/utilities');
                fetchOptions = fetchConfigUtil('json', { body: requestBody });
            } catch (e) {
                fetchOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: requestBody
                };
            }

            const response = await fetch(endpoint, fetchOptions);
            if (!response.ok) {
                if(response.status === 422){
                    if(typeof window.toastMessage.init === 'function'){
                        window.toastMessage.init(
                            "Can not add product more than availbale quantity!",
                            "error",
                            "top center",
                            "right to left",
                            3000
                          );
                    }
                }
                throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
            }

            const responseText = await response.text();
            const cartData = JSON.parse(responseText);
            window.latestCartData = cartData;
            // Update quantity from response if available
            if (cartData.items && Array.isArray(cartData.items)) {
                const updatedItem = cartData.items.find((/** @type {any} */ item) => item && item.key === this._lineKey);
                if (updatedItem && typeof updatedItem.quantity === 'number') {
                    this._quantity = updatedItem.quantity;
                    this._updateCountDisplay();
                }
            }

            // If we have section HTML in response, use it to update the section
            if (sectionId && cartData.sections && cartData.sections[sectionId]) {
                try {
                    // Try to import morphSection
                    try {
                        const { morphSection: morphSectionUtil } = await import('@theme/section-renderer');
                        await morphSectionUtil(sectionId, cartData.sections[sectionId]);

                        // Reload SmartWishlist after cart drawer re-render
                        if (typeof ReloadSmartWishlist === 'function') {
                            ReloadSmartWishlist();
                        }
                    } catch (morphError) {
                        // Fallback to sectionRenderer
                        try {
                            const { sectionRenderer: sectionRendererUtil } = await import('@theme/section-renderer');
                            await sectionRendererUtil.renderSection(sectionId, { cache: false });

                            // Reload SmartWishlist after cart drawer re-render
                            if (typeof ReloadSmartWishlist === 'function') {
                                ReloadSmartWishlist();
                            }
                        } catch (renderError) {
                            // Final fallback: manually update the section
                            await this._handleZeroQuantity();
                        }
                    }
                } catch (sectionError) {
                    console.warn('custom-cart-controllers: Section update failed, using fallback:', sectionError);
                    // Fallback to manual section re-render
                    if (newQuantity === 0) {
                        await this._handleZeroQuantity();
                    }
                }
            } else if (newQuantity === 0) {
                // If no section data, use fallback method
                await this._handleZeroQuantity();
            }

            // Dispatch custom event on document (not 'this') because morphSection
            // may have detached this element from the DOM, so bubbling won't reach document
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: cartData,
                bubbles: true,
                composed: true
            }));
            // Initialize choice goes well with if it exists
            if (typeof window.choiceGoesWellWith.init === 'function') {
                window.choiceGoesWellWith.init(cartData.items[0].product_id);
            }
            // Choice goes well with mobile/desktop content sync
            if(typeof window.syncMobileDesktopChoiceContent === 'function') {
                window.syncMobileDesktopChoiceContent();
            }
            // Show all button activation
            if(typeof window.activateShowAllButton === 'function') {
                window.activateShowAllButton();
            }
            // Wishlist re init
            if(window.customCartWishlistInstance.init){
                window.customCartWishlistInstance.init()
            }
            // Reset upsell products
            const upsellEl = document.querySelector('upsell-products');
            if (upsellEl && typeof upsellEl.reset === 'function') {
                upsellEl.reset();
            } else if (upsellEl && typeof upsellEl.connectedCallback === 'function') {
                // Fallback for older versions
                upsellEl.connectedCallback();
            }

            if(typeof document.querySelector('custom-from-your-wishlist')?.reset() === 'function'){
                document.querySelector('custom-from-your-wishlist')?.reset()
            }
        } catch (error) {
            console.error('Cart update error:', error);
            // Revert quantity
            this._quantity = previousQuantity;
            this._updateCountDisplay();
        } finally {
            this._setLoadingState(false);
            // Hide the loader when update is complete
            this._hideLoader();
        }
    }

    /**
     * @param {boolean} isLoading
     */
    _setLoadingState(isLoading) {
        this._isUpdating = isLoading;

        if (isLoading) {
            this.classList.add('is-loading');
        } else {
            this.classList.remove('is-loading');
        }

        if (!this.shadowRoot) return;
        const buttons = this.shadowRoot.querySelectorAll('button');
        buttons.forEach(button => {
            button.disabled = isLoading;
        });
    }

    /**
     * Shows the loader for the current cart item card by removing the 'hide' class
     */
    _showLoader() {
        const loader = this._loader || this.shadowRoot?.querySelector('.loader');
        if (loader) loader.classList.remove('hide');
    }

    /**
     * Hides the loader for the current cart item card by adding the 'hide' class
     */
    _hideLoader() {
        const loader = this._loader || this.shadowRoot?.querySelector('.loader');
        if (loader) loader.classList.add('hide');
    }

    /**
     * Handles the edge case when cart item count becomes 0.
     * Re-renders the cart drawer section to remove items with count 0.
     * @returns {Promise<void>}
     */
    async _handleZeroQuantity() {
        try {
            // Find the cart-items-component that contains this component
            const cartItemsComponent = this._findCartItemsComponent();

            if (!cartItemsComponent) {
                console.warn('custom-cart-controllers: cart-items-component not found, cannot re-render section');
                return;
            }

            // Get the section ID from the cart-items-component
            const sectionId = cartItemsComponent.dataset.sectionId;

            if (!sectionId) {
                console.warn('custom-cart-controllers: section ID not found on cart-items-component');
                return;
            }

            // Try to use the module import pattern (works if importmap is available)
            try {
                const { sectionRenderer } = await import('@theme/section-renderer');
                await sectionRenderer.renderSection(sectionId, { cache: false });

                // Reload SmartWishlist after cart drawer re-render
                if (typeof ReloadSmartWishlist === 'function') {
                    ReloadSmartWishlist();
                }
            } catch (importError) {
                // Fallback: Use direct fetch to re-render the section
                console.warn('custom-cart-controllers: Could not import sectionRenderer, using fallback method');
                await this._renderSectionFallback(sectionId);

                // Reload SmartWishlist after cart drawer re-render
                if (typeof ReloadSmartWishlist === 'function') {
                    ReloadSmartWishlist();
                }
            }
        } catch (error) {
            console.error('custom-cart-controllers: Error re-rendering cart drawer section:', error);
        }
    }

    /**
     * Finds the cart-items-component that contains this component
     * @returns {HTMLElement|null}
     */
    _findCartItemsComponent() {
        // Traverse up the DOM tree to find cart-items-component
        let currentElement = this.parentElement;

        while (currentElement) {
            if (currentElement.tagName === 'CART-ITEMS-COMPONENT' ||
                currentElement.classList.contains('cart-items-component')) {
                return currentElement;
            }
            currentElement = currentElement.parentElement;
        }

        // Also check if there's a cart-items-component in the document
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        if (cartItemsComponents.length > 0) {
            // Return the first one (usually there's only one cart drawer)
            const firstComponent = cartItemsComponents[0];
            return firstComponent instanceof HTMLElement ? firstComponent : null;
        }

        return null;
    }

    /**
     * Fallback method to re-render section using direct fetch
     * @param {string} sectionId
     * @returns {Promise<void>}
     */
    async _renderSectionFallback(sectionId) {
        try {
            // Build section rendering URL
            const url = new URL(window.location.href);
            const normalizedSectionId = sectionId.replace(/^shopify-section-/, '');
            url.searchParams.set('section_id', normalizedSectionId);

            // Fetch the section HTML
            const response = await fetch(url.toString());
            if (!response.ok) {
                throw new Error(`Failed to fetch section: ${response.status}`);
            }

            const sectionHTML = await response.text();

            // Parse and morph the section
            const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
            const sectionSelector = `shopify-section-${normalizedSectionId}`;
            const existingElement = document.getElementById(sectionSelector);
            const newElement = fragment.getElementById(sectionSelector);

            if (!existingElement || !newElement) {
                throw new Error(`Section ${sectionId} not found in DOM or response`);
            }

            // Try to import morphSection for proper morphing
            try {
                const { morphSection } = await import('@theme/section-renderer');
                await morphSection(sectionId, sectionHTML);

                // Reload SmartWishlist after cart drawer re-render
                if (typeof ReloadSmartWishlist === 'function') {
                    ReloadSmartWishlist();
                }
            } catch (morphError) {
                // If morphSection import fails, try to import morph directly
                try {
                    const { morph } = await import('@theme/morph');
                    morph(existingElement, newElement);

                    // Reload SmartWishlist after cart drawer re-render
                    if (typeof ReloadSmartWishlist === 'function') {
                        ReloadSmartWishlist();
                    }
                } catch (morphImportError) {
                    // Final fallback: simple replacement
                    existingElement.innerHTML = newElement.innerHTML;

                    // Reload SmartWishlist after cart drawer re-render
                    if (typeof ReloadSmartWishlist === 'function') {
                        ReloadSmartWishlist();
                    }
                }
            }
        } catch (error) {
            console.error('custom-cart-controllers: Fallback section rendering failed:', error);
        }
    }
}

customElements.define('custom-cart-controllers', CustomCartControllers);
